import parse, { type DOMNode, Element } from 'html-react-parser';
import * as React from 'react';
import sanitize from 'sanitize-html';

const ALLOWED_TAGS = [
  'article',
  'aside',
  'footer',
  'header',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'hgroup',
  'section',
  'blockquote',
  'dd',
  'div',
  'dl',
  'dt',
  'figcaption',
  'figure',
  'hr',
  'li',
  'main',
  'ol',
  'p',
  'ul',
  'a',
  'b',
  'br',
  'cite',
  'code',
  'em',
  'i',
  'img',
  'mark',
  'small',
  'span',
  'strong',
  'sub',
  'sup',
  'time',
  'caption',
  'table',
  'tbody',
  'td',
  'tfoot',
  'th',
  'thead',
  'tr',
] as const;

function anchorAttribs(
  attribs: Record<string, string>,
): Record<string, string> {
  const next: Record<string, string> = {};

  if (typeof attribs.href === 'string' && attribs.href.length > 0) {
    next.href = attribs.href;
  }
  if (typeof attribs.title === 'string' && attribs.title.length > 0) {
    next.title = attribs.title;
  }
  if (typeof attribs.target === 'string' && attribs.target.length > 0) {
    next.target = attribs.target;
  }
  if (attribs.target === '_blank') {
    next.rel = 'noopener noreferrer';
  }

  return next;
}

const SANITIZE_OPTIONS: sanitize.IOptions = {
  allowedTags: [...ALLOWED_TAGS],
  allowedAttributes: {
    a: ['href', 'title', 'target', 'rel'],
    img: ['src', 'alt', 'width', 'height'],
    td: ['colspan', 'rowspan'],
    th: ['colspan', 'rowspan'],
  },
  allowedSchemes: ['http', 'https', 'mailto'],
  allowProtocolRelative: false,
  transformTags: {
    a: (_tag, attribs) => ({
      tagName: 'a',
      attribs: anchorAttribs(attribs),
    }),
  },
};

function normalizeMarkup(dirty: string): string {
  return dirty
    .replace(/(\r\n|\n|\r|\t)/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function sanitizeMarkup(dirty: string): string {
  return sanitize(normalizeMarkup(dirty), SANITIZE_OPTIONS);
}

/**
 * Returns a sanitized HTML **string** suitable for `dangerouslySetInnerHTML`
 * (or for hen-ui TextCell `html: true`). Same allowlist and
 * `rel="noopener noreferrer"` transform as {@link parseHtmlString}.
 */
export function sanitizeHtmlString(dirty: string): string {
  if (!dirty) {
    return '';
  }
  return sanitizeMarkup(dirty);
}

function isAllowedElement(node: DOMNode): boolean {
  if (!(node instanceof Element)) {
    return true;
  }
  return ALLOWED_TAGS.includes(node.name as (typeof ALLOWED_TAGS)[number]);
}

/**
 * Sanitizes and parses CMS/marketing HTML from Offerings or Organization
 * Marketing into React nodes. Prefer {@link RichText} for rendering so block
 * markup uses a valid container.
 */
const parseHtmlString = (
  dirty: string,
): string | React.JSX.Element | React.JSX.Element[] | null => {
  if (!dirty) {
    return null;
  }

  const parsed = parse(sanitizeMarkup(dirty), {
    replace: (domNode) => {
      if (domNode instanceof Element && !isAllowedElement(domNode)) {
        return React.createElement(React.Fragment);
      }
    },
  });

  if (Array.isArray(parsed)) {
    return parsed;
  }

  return parsed;
};

export default parseHtmlString;
